import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Header from '../components/Header';
import { ChevronDown, HelpCircle, MapPin, Plane, BookOpen, Shield, Settings, Smartphone, Briefcase, Globe, ArrowLeft, Headphones } from 'lucide-react';

const faqData = [
  {
    category: 'Getting Started',
    icon: <MapPin className="w-5 h-5" />,
    questions: [
      {
        q: 'How do I create my first trip itinerary?',
        a: 'Go to Plan > New Trip in the header. Fill in your destination, travel dates, budget, and preferences. Our AI will generate a complete day-by-day itinerary with activities, lodging recommendations, transport options, and budget estimates.',
      },
      {
        q: 'Do I need to create an account to use the app?',
        a: 'Yes, a free account is required to create and save itineraries. You can register with your email and password. Your trips, journals, and blogs are all saved securely to your account.',
      },
      {
        q: 'Can I plan trips to multiple cities or countries?',
        a: 'Absolutely! When creating a new trip, you can add multiple destination cities across different countries. The AI will intelligently distribute your travel days and plan transport between cities.',
      },
      {
        q: 'How do I navigate the app?',
        a: 'The header menu is organized into clear sections: "Plan" for trip planning tools (New Trip, Templates, Budget Calculator, Compare, Packing List, Visa Check), "Book" for booking services (Flights, Hotels, Tour Packages, Transfers, Car Rentals, and more), "Dashboard" for your saved trips and content, "Assisted Services" for personal help, and "Contact" to reach us directly.',
      },
    ],
  },
  {
    category: 'Trip Planning & AI',
    icon: <Plane className="w-5 h-5" />,
    questions: [
      {
        q: 'How does the AI itinerary generation work?',
        a: 'We use Google Gemini AI to create personalized itineraries based on your travel style, interests, budget, and dates. The AI considers real hotels, local attractions, and seasonal events to craft a detailed day-by-day plan.',
      },
      {
        q: 'Can I edit the AI-generated itinerary?',
        a: 'Yes! You can edit your trip details, modify activities in the Trip Story view, and the itinerary will update accordingly. You can also add personal notes and photos for each day.',
      },
      {
        q: 'What is the Budget Calculator?',
        a: 'Found under Plan > Budget Calculator, it lets you track actual expenses against the AI-estimated budget. You can add expenses by category (accommodation, food, transport, activities, flights) and view a detailed comparison dashboard showing where you are over or under budget.',
      },
      {
        q: 'Can I compare different trip destinations?',
        a: 'Yes! Go to Plan > Compare Destinations to view side-by-side comparisons of your planned trips, including budget breakdowns, weather, and itinerary highlights.',
      },
      {
        q: 'What are Trip Templates?',
        a: 'Found under Plan > Templates, these are pre-made itinerary plans for popular destinations. You can browse curated templates or community-published ones, filtered by region, duration, budget, and traveler type. You can also publish your own completed trips as templates for other travelers.',
      },
      {
        q: 'How does the Packing List preparation work?',
        a: 'Go to Plan > Packing List. The feature generates a customized packing checklist based on your destination, travel dates, weather forecast, and trip style. It covers clothing, toiletries, documents, electronics, and destination-specific essentials. You can check off items as you pack.',
      },
    ],
  },
  {
    category: 'Visa Check & Travel Documents',
    icon: <Globe className="w-5 h-5" />,
    questions: [
      {
        q: 'How does the Visa Check feature work?',
        a: 'Go to Plan > Visa Check in the header. Enter your nationality and the country you want to visit. The AI will assess visa requirements including visa type, maximum stay duration, processing time, required documents, and estimated cost. If a visa is required, an application link is provided.',
      },
      {
        q: 'Does the itinerary also include visa information?',
        a: 'Yes! When generating an itinerary, the AI includes visa requirements based on your nationality and destination. The Visa tab on the itinerary page shows all the required documents, processing details, and links to apply.',
      },
      {
        q: 'How reliable is the visa information provided?',
        a: 'The visa information is AI-generated based on current knowledge of immigration requirements. We always recommend verifying the details with the official embassy or consulate website of your destination country before applying.',
      },
    ],
  },
  {
    category: 'Trip Story & Blog',
    icon: <BookOpen className="w-5 h-5" />,
    questions: [
      {
        q: 'What is the Trip Story feature?',
        a: 'Trip Story is a timeline view of your trip where you can add photos, personal notes, and edit activities for each day. It creates a visual story of your travel experience that you can share with friends and family. Access it via Dashboard > Trip Story.',
      },
      {
        q: 'How do I create an AI-generated blog from my trip?',
        a: 'Open your completed trip, click the "Blog" button, and the AI will automatically generate a rich travel blog based on your itinerary, photos, and personal notes. You can edit sections, add images, and customize the cover.',
      },
      {
        q: 'Can I download my Trip Story or Blog as a PDF?',
        a: 'Yes! Both Trip Story and Blog have download options. You can generate a PDF of your timeline or blog and share it offline or print it as a travel memento. The download option is also available in the share dialog from the Dashboard.',
      },
      {
        q: 'How do I share my trip with others?',
        a: 'Each Trip Story and Blog has a Share button that generates a unique link. You can share via WhatsApp, Twitter, Facebook, Email, or copy the link directly. Recipients can view your trip without needing an account.',
      },
    ],
  },
  {
    category: 'Booking Services',
    icon: <Briefcase className="w-5 h-5" />,
    questions: [
      {
        q: 'How do I book flights and hotels?',
        a: 'Go to Book > Flights or Book > Hotels in the header. We connect you with trusted booking platforms including Expedia, Aviasales, TraveLean for flights, and Booking.com and Expedia for hotels. You can search and compare options directly.',
      },
      {
        q: 'Can I book airport transfers and local trips?',
        a: 'Yes! Under Book > Airport Transfers or Book > Local Trips, you can pre-book reliable pickups, intercity transfers, and local rides through providers like Welcome Pickups, GetTransfer, Kiwitaxi, and Intui Travel.',
      },
      {
        q: 'Does Triple A offer car rental services?',
        a: 'Yes! Go to Book > Car Rentals to compare deals from providers like Discovercars, Qeeq, Localrent, Economybookings, and GetRentacar. Find the best prices with free cancellation and no hidden fees.',
      },
      {
        q: 'Can I book tickets for activities and attractions?',
        a: 'Go to Book > Activities & Attractions to find and book skip-the-line tickets for museums, theme parks, cultural shows, and sightseeing experiences through providers like Tiqets and GetYourGuide.',
      },
      {
        q: 'Does the app offer travel insurance?',
        a: 'Yes! Visit Book > Travel Insurance to compare plans from providers like Ekta, SafetyWing, and World Nomads. Coverage includes medical emergencies, trip cancellations, lost baggage, and adventure activities.',
      },
      {
        q: 'What about flight delay or cancellation claims?',
        a: 'Go to Book > Flight Compensation. Providers like AirHelp and Compensair can help you claim up to $600 for delayed, cancelled, or overbooked flights. It is a no-win, no-fee process — check your eligibility in minutes.',
      },
      {
        q: 'Can I get an eSIM for my international trip?',
        a: 'Yes! Visit Book > eSIM & Connectivity for travel eSIM plans from Yesim, Airalo, and DrimSim. Stay connected in 150+ countries without expensive roaming — buy and install in minutes, no physical SIM needed.',
      },
      {
        q: 'Is there a luggage storage service?',
        a: 'Go to Book > Luggage Storage. Radical Storage offers secure, flat-rate luggage storage near major stations, airports, and tourist attractions worldwide. Store your bags and explore hands-free.',
      },
      {
        q: 'Can I book local tour packages?',
        a: 'Yes! Visit Book > Tour Packages to browse and book guided city tours, day trips, adventure activities, food tours, and cultural experiences at your destination.',
      },
      {
        q: 'What is the currency conversion feature?',
        a: 'The app includes a live currency converter powered by real-time exchange rates. You can convert between currencies to help plan your budget and understand local pricing at your destination.',
      },
    ],
  },
  {
    category: 'Assisted Services',
    icon: <Headphones className="w-5 h-5" />,
    questions: [
      {
        q: 'What are Assisted Services?',
        a: 'Assisted Services provide personalised, hands-on travel support from our experts. Instead of booking on your own, our team handles everything for you — from finding the best flights and hotels to visa guidance and custom itinerary planning.',
      },
      {
        q: 'What kind of help can I get?',
        a: 'Our Assisted Services cover: Flight Booking Assistance, Hotel Reservation Support, Visa & Documentation Help, Custom Tour Packages, Travel Insurance Guidance, and Group, Event & Corporate Travel coordination.',
      },
      {
        q: 'How do I contact the Assisted Services team?',
        a: 'Visit the Assisted Services page from the header. You can reach us by phone at +61 401 050 457 or +61 424 024 078, email at triplea@tripleabooking.com, or chat with us directly on WhatsApp for quick responses.',
      },
      {
        q: 'Is there a fee for Assisted Services?',
        a: 'Consultation is free. Fees vary depending on the service — for example, custom tour planning or corporate travel coordination may involve a service charge. Our team will discuss all costs upfront before proceeding.',
      },
      {
        q: 'Can you help with group or corporate travel?',
        a: 'Absolutely! We specialise in group, event, and corporate travel. Whether it is a team retreat, conference, wedding destination, or family reunion, we coordinate multi-traveller bookings, group activities, and seamless logistics.',
      },
    ],
  },
  {
    category: 'Account & Data',
    icon: <Shield className="w-5 h-5" />,
    questions: [
      {
        q: 'How is my data stored and secured?',
        a: 'Your data is stored securely in our database with encrypted passwords. Photos are stored in secure cloud object storage. We never share your personal information with third parties.',
      },
      {
        q: 'Can I invite travel companions to my trip?',
        a: 'Yes! From the itinerary page, you can invite companions by email. They will receive a notification and can view the shared itinerary to collaborate on trip planning.',
      },
      {
        q: 'What is the Photo Journal?',
        a: 'The Photo Journal lets you upload and organize travel photos by day and trip. You can add captions, and your photos are automatically included in Trip Stories and Blogs when you generate them. Access it via Dashboard > My Journal.',
      },
    ],
  },
  {
    category: 'Mobile & Offline',
    icon: <Smartphone className="w-5 h-5" />,
    questions: [
      {
        q: 'Can I use the app on my phone?',
        a: 'Yes! The app is a Progressive Web App (PWA) that works great on mobile browsers. You can install it to your home screen for an app-like experience with offline support.',
      },
      {
        q: 'Does the app work offline?',
        a: 'The app caches your trip data for offline access. You can view your saved itineraries without an internet connection. New trips and photo uploads require connectivity.',
      },
      {
        q: 'How do I install the app on iOS?',
        a: 'Open the app in Safari, tap the Share button at the bottom of the screen, then select "Add to Home Screen." The app will appear as an icon on your home screen with offline capabilities.',
      },
    ],
  },
];

const FAQItem = ({ question, answer, isOpen, onClick }) => (
  <div className="border-b border-white/10 last:border-0">
    <button
      onClick={onClick}
      className="w-full flex items-center justify-between py-4 px-1 text-left group"
      data-testid={`faq-question-${question.slice(0, 20).replace(/\s+/g, '-').toLowerCase()}`}
    >
      <span className="text-white/90 font-medium text-sm sm:text-base pr-4 group-hover:text-gold transition-colors">
        {question}
      </span>
      <ChevronDown className={`w-4 h-4 text-gold flex-shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
    </button>
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="overflow-hidden"
        >
          <p className="text-white/60 text-sm leading-relaxed pb-4 px-1">{answer}</p>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

export default function FAQPage() {
  const [openItems, setOpenItems] = useState({});
  const navigate = useNavigate();

  const toggle = (key) => {
    setOpenItems(prev => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <div className="min-h-screen relative">
      <div className="fixed inset-0 -z-10 bg-gradient-to-br from-ocean via-ocean-light/90 to-sky/70" />
      <Header />

      <main className="pt-24 pb-20 px-4 sm:px-6 md:px-12 lg:px-24">
        <div className="max-w-4xl mx-auto">
          <div className="mb-6">
            <button onClick={() => navigate('/dashboard')} className="text-white/70 hover:text-white text-sm flex items-center gap-1 transition-colors" data-testid="back-to-dashboard-faq">
              <ArrowLeft className="w-4 h-4" /> Back to Dashboard
            </button>
          </div>
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-12">
            <div className="w-16 h-16 bg-gold/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <HelpCircle className="w-8 h-8 text-gold" />
            </div>
            <h1 className="font-serif text-3xl sm:text-4xl text-white font-bold" data-testid="faq-page-title">
              Frequently Asked Questions
            </h1>
            <p className="text-white/60 text-sm mt-3 max-w-lg mx-auto">
              Everything you need to know about planning your perfect trip with Triple A Travel Solutions.
            </p>
          </motion.div>

          <div className="space-y-6">
            {faqData.map((section, sIdx) => (
              <motion.div
                key={section.category}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: sIdx * 0.08 }}
                className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl overflow-hidden"
                data-testid={`faq-section-${section.category.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className="flex items-center gap-3 px-6 pt-5 pb-2">
                  <div className="w-9 h-9 bg-gold/20 rounded-lg flex items-center justify-center text-gold">
                    {section.icon}
                  </div>
                  <h2 className="font-serif text-lg text-white font-semibold">{section.category}</h2>
                </div>
                <div className="px-6 pb-3">
                  {section.questions.map((item, qIdx) => {
                    const key = `${sIdx}-${qIdx}`;
                    return (
                      <FAQItem
                        key={key}
                        question={item.q}
                        answer={item.a}
                        isOpen={!!openItems[key]}
                        onClick={() => toggle(key)}
                      />
                    );
                  })}
                </div>
              </motion.div>
            ))}
          </div>

          <div className="flex justify-center mt-8">
            <button onClick={() => navigate('/dashboard')} className="text-white/70 hover:text-white border border-white/30 hover:bg-white/10 rounded-full px-6 py-2 text-sm font-medium flex items-center gap-2 transition-all" data-testid="back-to-dashboard-faq-bottom">
              <ArrowLeft className="w-4 h-4" /> Back to Dashboard
            </button>
          </div>
        </div>
      </main>
    </div>
  );
}
